import React, { useState, useEffect, useRef } from 'react';
import { recipientAPI, companyAPI, importAPI } from '../api';

function Recipients() {
  const [recipients, setRecipients] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showRecipientModal, setShowRecipientModal] = useState(false);
  const [showCompanyModal, setShowCompanyModal] = useState(false);
  const [editingRecipient, setEditingRecipient] = useState(null);
  const [editingCompany, setEditingCompany] = useState(null);
  const [viewMode, setViewMode] = useState('recipients'); // 'recipients' or 'companies'
  
  const [recipientForm, setRecipientForm] = useState({
    email: '',
    name: '',
    company_id: '',
    tags: '',
    notes: '',
  });
  
  const [companyForm, setCompanyForm] = useState({
    name: '',
    meeting_status: '',
    notes: '',
  });
  
  const [message, setMessage] = useState({ type: '', text: '' });
  const [importing, setImporting] = useState(false);
  const csvInputRef = useRef(null);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [filter, setFilter] = useState({ 
    status: '', 
    tag: '', 
    search: '',
    company_id: ''
  });

  useEffect(() => {
    loadData();
  }, [page, filter, viewMode]);

  const loadData = async () => {
    try {
      if (viewMode === 'recipients') {
        const params = { page, per_page: 20, ...filter };
        const response = await recipientAPI.getAll(params);
        if (response.data.success) {
          setRecipients(response.data.data);
          setTotal(response.data.total);
        }
      }
      
      // 总是加载公司列表
      const companyResponse = await companyAPI.getAll({});
      if (companyResponse.data.success) {
        setCompanies(companyResponse.data.data);
      }
    } catch (error) {
      console.error('加载数据失败:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleRecipientSubmit = async (e) => {
    e.preventDefault();
    try {
      const submitData = {
        ...recipientForm,
        company_id: recipientForm.company_id || null
      };
      
      if (editingRecipient) {
        await recipientAPI.update(editingRecipient.id, submitData);
        setMessage({ type: 'success', text: '更新成功！' });
      } else {
        await recipientAPI.add(submitData);
        setMessage({ type: 'success', text: '添加成功！' });
      }
      setShowRecipientModal(false);
      setEditingRecipient(null);
      setRecipientForm({ email: '', name: '', company_id: '', tags: '', notes: '' });
      loadData();
      setTimeout(() => setMessage({ type: '', text: '' }), 3000);
    } catch (error) {
      setMessage({ type: 'error', text: error.response?.data?.message || '操作失败' });
    }
  };

  const handleCompanySubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingCompany) {
        await companyAPI.update(editingCompany.id, companyForm);
        setMessage({ type: 'success', text: '更新成功！' });
      } else {
        await companyAPI.create(companyForm);
        setMessage({ type: 'success', text: '添加成功！' });
      }
      setShowCompanyModal(false);
      setEditingCompany(null);
      setCompanyForm({ name: '', meeting_status: '', notes: '' });
      loadData();
      setTimeout(() => setMessage({ type: '', text: '' }), 3000);
    } catch (error) {
      setMessage({ type: 'error', text: error.response?.data?.message || '操作失败' });
    }
  };

  const handleEditRecipient = (recipient) => {
    setEditingRecipient(recipient);
    setRecipientForm({
      email: recipient.email,
      name: recipient.name || '',
      company_id: recipient.company_id || '',
      tags: Array.isArray(recipient.tags) ? recipient.tags.join(', ') : recipient.tags || '',
      notes: recipient.notes || '',
    });
    setShowRecipientModal(true);
  };

  const handleEditCompany = (company) => {
    setEditingCompany(company);
    setCompanyForm({
      name: company.name,
      meeting_status: company.meeting_status || '',
      notes: company.notes || '',
    });
    setShowCompanyModal(true);
  };

  const handleDeleteRecipient = async (id) => {
    if (!window.confirm('确定要删除这个收件人吗？')) return;

    try {
      await recipientAPI.delete(id);
      setMessage({ type: 'success', text: '删除成功！' });
      loadData();
      setTimeout(() => setMessage({ type: '', text: '' }), 3000);
    } catch (error) {
      setMessage({ type: 'error', text: '删除失败' });
    }
  };

  const handleDeleteCompany = async (id) => {
    if (!window.confirm('确定要删除这个公司/律所吗？关联的收件人将不再关联到此公司。')) return;

    try {
      await companyAPI.delete(id);
      setMessage({ type: 'success', text: '删除成功！' });
      loadData();
      setTimeout(() => setMessage({ type: '', text: '' }), 3000);
    } catch (error) {
      setMessage({ type: 'error', text: '删除失败' });
    }
  };

  const handleStatusChange = async (recipient, newStatus) => {
    try {
      await recipientAPI.update(recipient.id, { status: newStatus });
      setMessage({ type: 'success', text: '状态更新成功！' });
      loadData();
      setTimeout(() => setMessage({ type: '', text: '' }), 3000);
    } catch (error) {
      setMessage({ type: 'error', text: '状态更新失败' });
    }
  };

  const handleImportCSV = async (e) => {
    const file = e.target.files && e.target.files[0];
    if (!file) return;
    const tags = window.prompt('给这批导入的收件人加标签（逗号分隔，可留空）：', 'cos,uk-immigration');
    setImporting(true);
    try {
      const res = await importAPI.upload(file, tags || '');
      const d = res.data.data;
      setMessage({
        type: 'success',
        text: `导入完成：新增 ${d.created}，重复跳过 ${d.duplicates}，无效 ${d.invalid}，新建律所 ${d.firms_created}（共 ${d.total_rows} 行）`,
      });
      setPage(1);
      loadData();
      setTimeout(() => setMessage({ type: '', text: '' }), 8000);
    } catch (error) {
      setMessage({ type: 'error', text: '导入失败：' + (error.response?.data?.message || error.message) });
    } finally {
      setImporting(false);
      if (csvInputRef.current) csvInputRef.current.value = '';
    }
  };

  const getStatusBadge = (status) => {
    const badges = {
      active: 'badge-info',
      replied: 'badge-success',
      connected: 'badge-success',
      unsubscribed: 'badge-secondary',
    };
    return `badge ${badges[status] || 'badge-secondary'}`;
  };

  const getMeetingStatusBadge = (status) => {
    const badges = {
      met_recommend: 'badge-success',
      met_no_intent: 'badge-warning',
    };
    return `badge ${badges[status] || 'badge-secondary'}`;
  };

  const getMeetingStatusLabel = (status) => {
    const labels = {
      met_recommend: '已开会/建议联系',
      met_no_intent: '开过会但没达成意向',
    };
    return labels[status] || '-';
  };

  const statusOptions = [
    { value: 'active', label: '活跃' },
    { value: 'replied', label: '已回复' },
    { value: 'connected', label: '已连接' },
    { value: 'unsubscribed', label: '已退订' },
  ];

  const meetingStatusOptions = [
    { value: '', label: '无标记' },
    { value: 'met_recommend', label: '已开会/建议联系' },
    { value: 'met_no_intent', label: '开过会但没达成意向' },
  ];

  if (loading) {
    return <div>加载中...</div>;
  }

  return (
    <div>
      <div className="page-header">
        <h1>收件人管理</h1>
        <p>管理您的邮件收件人、公司/律所分组和标签</p>
      </div>

      {message.text && (
        <div className={`alert alert-${message.type}`}>
          {message.text}
        </div>
      )}

      {/* 视图切换 */}
      <div style={{ marginBottom: '20px', display: 'flex', gap: '10px' }}>
        <button
          className={`btn ${viewMode === 'recipients' ? 'btn-primary' : 'btn-secondary'}`}
          onClick={() => setViewMode('recipients')}
        >
          收件人列表
        </button>
        <button
          className={`btn ${viewMode === 'companies' ? 'btn-primary' : 'btn-secondary'}`}
          onClick={() => setViewMode('companies')}
        >
          公司/律所管理
        </button>
      </div>

      {/* 收件人视图 */}
      {viewMode === 'recipients' && (
        <div className="card">
          <div className="card-header">
            <h2>收件人列表 ({total})</h2>
            <div style={{ display: 'flex', gap: '8px' }}>
              <input
                ref={csvInputRef}
                type="file"
                accept=".csv"
                style={{ display: 'none' }}
                onChange={handleImportCSV}
              />
              <button
                className="btn btn-secondary"
                disabled={importing}
                onClick={() => csvInputRef.current && csvInputRef.current.click()}
                title="CSV 列名支持: firm_name/contact_person/email/title/website/city/phone/tags"
              >
                {importing ? '导入中…' : '📥 导入 CSV'}
              </button>
              <button
                className="btn btn-primary"
                onClick={() => {
                  setEditingRecipient(null);
                  setRecipientForm({ email: '', name: '', company_id: '', tags: '', notes: '' });
                  setShowRecipientModal(true);
                }}
              >
                + 添加收件人
              </button>
            </div>
          </div>

          <div style={{ padding: '20px', borderBottom: '1px solid #eee', display: 'flex', gap: '15px', flexWrap: 'wrap' }}>
            <div>
              <input
                type="text"
                placeholder="搜索邮箱或姓名..."
                value={filter.search}
                onChange={(e) => setFilter({ ...filter, search: e.target.value })}
                style={{ padding: '8px', borderRadius: '5px', border: '1px solid #ddd', width: '250px' }}
              />
            </div>
            <div>
              <select
                value={filter.status}
                onChange={(e) => setFilter({ ...filter, status: e.target.value })}
                style={{ padding: '8px', borderRadius: '5px', border: '1px solid #ddd' }}
              >
                <option value="">所有状态</option>
                {statusOptions.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <input
                type="text"
                placeholder="按标签筛选"
                value={filter.tag}
                onChange={(e) => setFilter({ ...filter, tag: e.target.value })}
                style={{ padding: '8px', borderRadius: '5px', border: '1px solid #ddd' }}
              />
            </div>
            <div>
              <select
                value={filter.company_id}
                onChange={(e) => setFilter({ ...filter, company_id: e.target.value })}
                style={{ padding: '8px', borderRadius: '5px', border: '1px solid #ddd' }}
              >
                <option value="">所有公司</option>
                {companies.map((company) => (
                  <option key={company.id} value={company.id}>
                    {company.name}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {recipients.length === 0 ? (
            <p style={{ textAlign: 'center', padding: '40px', color: '#999' }}>
              暂无收件人
            </p>
          ) : (
            <>
              <table className="table">
                <thead>
                  <tr>
                    <th>邮箱地址</th>
                    <th>姓名</th>
                    <th>公司/律所</th>
                    <th>标签</th>
                    <th>状态</th>
                    <th>最后联系</th>
                    <th>操作</th>
                  </tr>
                </thead>
                <tbody>
                  {recipients.map((recipient) => (
                    <tr key={recipient.id}>
                      <td>{recipient.email}</td>
                      <td>{recipient.name || '-'}</td>
                      <td>
                        {recipient.company ? (
                          <div>
                            <div style={{ fontWeight: 'bold' }}>{recipient.company.name}</div>
                            {recipient.company.meeting_status && (
                              <span className={getMeetingStatusBadge(recipient.company.meeting_status)} style={{ fontSize: '11px', marginTop: '3px' }}>
                                {getMeetingStatusLabel(recipient.company.meeting_status)}
                              </span>
                            )}
                          </div>
                        ) : '-'}
                      </td>
                      <td>
                        {Array.isArray(recipient.tags) && recipient.tags.length > 0
                          ? recipient.tags.map((tag, i) => (
                              <span key={i} className="badge badge-info" style={{ marginRight: '5px' }}>
                                {tag}
                              </span>
                            ))
                          : '-'}
                      </td>
                      <td>
                        <select
                          value={recipient.status}
                          onChange={(e) => handleStatusChange(recipient, e.target.value)}
                          className={getStatusBadge(recipient.status)}
                          style={{ border: 'none', background: 'transparent', cursor: 'pointer' }}
                        >
                          {statusOptions.map((opt) => (
                            <option key={opt.value} value={opt.value}>
                              {opt.label}
                            </option>
                          ))}
                        </select>
                      </td>
                      <td>
                        {recipient.last_contact
                          ? new Date(recipient.last_contact).toLocaleDateString()
                          : '-'}
                      </td>
                      <td>
                        <button
                          className="btn btn-primary"
                          style={{ padding: '5px 10px', fontSize: '12px', marginRight: '5px' }}
                          onClick={() => handleEditRecipient(recipient)}
                        >
                          编辑
                        </button>
                        <button
                          className="btn btn-danger"
                          style={{ padding: '5px 10px', fontSize: '12px' }}
                          onClick={() => handleDeleteRecipient(recipient.id)}
                        >
                          删除
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>

              <div className="pagination">
                <button onClick={() => setPage(page - 1)} disabled={page === 1}>
                  上一页
                </button>
                <span>第 {page} 页</span>
                <button onClick={() => setPage(page + 1)} disabled={recipients.length < 20}>
                  下一页
                </button>
              </div>
            </>
          )}
        </div>
      )}

      {/* 公司/律所视图 */}
      {viewMode === 'companies' && (
        <div className="card">
          <div className="card-header">
            <h2>公司/律所管理 ({companies.length})</h2>
            <button
              className="btn btn-primary"
              onClick={() => {
                setEditingCompany(null);
                setCompanyForm({ name: '', meeting_status: '', notes: '' });
                setShowCompanyModal(true);
              }}
            >
              + 添加公司/律所
            </button>
          </div>

          {companies.length === 0 ? (
            <p style={{ textAlign: 'center', padding: '40px', color: '#999' }}>
              暂无公司/律所
            </p>
          ) : (
            <div style={{ padding: '20px' }}>
              {companies.map((company) => (
                <div key={company.id} style={{ 
                  border: '1px solid #e0e0e0', 
                  borderRadius: '8px', 
                  padding: '15px', 
                  marginBottom: '15px',
                  backgroundColor: '#f9f9f9'
                }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', marginBottom: '10px' }}>
                    <div>
                      <h3 style={{ margin: '0 0 8px 0', fontSize: '18px' }}>{company.name}</h3>
                      {company.meeting_status && (
                        <span className={getMeetingStatusBadge(company.meeting_status)}>
                          {getMeetingStatusLabel(company.meeting_status)}
                        </span>
                      )}
                      {company.notes && (
                        <p style={{ margin: '8px 0 0 0', fontSize: '13px', color: '#666' }}>
                          备注: {company.notes}
                        </p>
                      )}
                    </div>
                    <div>
                      <button
                        className="btn btn-primary"
                        style={{ padding: '5px 10px', fontSize: '12px', marginRight: '5px' }}
                        onClick={() => handleEditCompany(company)}
                      >
                        编辑
                      </button>
                      <button
                        className="btn btn-danger"
                        style={{ padding: '5px 10px', fontSize: '12px' }}
                        onClick={() => handleDeleteCompany(company.id)}
                      >
                        删除
                      </button>
                    </div>
                  </div>
                  
                  {company.recipients && company.recipients.length > 0 && (
                    <div style={{ 
                      marginTop: '12px', 
                      paddingTop: '12px', 
                      borderTop: '1px solid #e0e0e0',
                      backgroundColor: '#fff',
                      padding: '10px',
                      borderRadius: '5px'
                    }}>
                      <div style={{ fontSize: '13px', color: '#666', marginBottom: '8px', fontWeight: 'bold' }}>
                        联系人 ({company.recipients.length})：
                      </div>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px' }}>
                        {company.recipients.map((person) => (
                          <div key={person.id} style={{ 
                            padding: '6px 12px', 
                            backgroundColor: '#e3f2fd', 
                            borderRadius: '15px',
                            fontSize: '13px',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '5px'
                          }}>
                            <span style={{ fontWeight: '500' }}>{person.name || '未命名'}</span>
                            <span style={{ color: '#666' }}>({person.email})</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* 收件人模态框 */}
      {showRecipientModal && (
        <div className="modal-overlay" onClick={() => setShowRecipientModal(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{editingRecipient ? '编辑收件人' : '添加收件人'}</h2>
              <button className="modal-close" onClick={() => setShowRecipientModal(false)}>
                ×
              </button>
            </div>

            <form onSubmit={handleRecipientSubmit}>
              <div className="form-group">
                <label>邮箱地址 *</label>
                <input
                  type="email"
                  value={recipientForm.email}
                  onChange={(e) => setRecipientForm({ ...recipientForm, email: e.target.value })}
                  placeholder="recipient@example.com"
                  required
                  disabled={!!editingRecipient}
                />
              </div>

              <div className="form-group">
                <label>姓名</label>
                <input
                  type="text"
                  value={recipientForm.name}
                  onChange={(e) => setRecipientForm({ ...recipientForm, name: e.target.value })}
                  placeholder="收件人姓名"
                />
              </div>

              <div className="form-group">
                <label>公司/律所</label>
                <select
                  value={recipientForm.company_id}
                  onChange={(e) => setRecipientForm({ ...recipientForm, company_id: e.target.value })}
                >
                  <option value="">不关联公司</option>
                  {companies.map((company) => (
                    <option key={company.id} value={company.id}>
                      {company.name}
                    </option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label>标签（用逗号分隔）</label>
                <input
                  type="text"
                  value={recipientForm.tags}
                  onChange={(e) => setRecipientForm({ ...recipientForm, tags: e.target.value })}
                  placeholder="例如: 潜在客户, VIP, 已沟通"
                />
              </div>

              <div className="form-group">
                <label>备注</label>
                <textarea
                  value={recipientForm.notes}
                  onChange={(e) => setRecipientForm({ ...recipientForm, notes: e.target.value })}
                  placeholder="添加备注信息..."
                />
              </div>

              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => setShowRecipientModal(false)}>
                  取消
                </button>
                <button type="submit" className="btn btn-primary">
                  {editingRecipient ? '更新' : '添加'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* 公司模态框 */}
      {showCompanyModal && (
        <div className="modal-overlay" onClick={() => setShowCompanyModal(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{editingCompany ? '编辑公司/律所' : '添加公司/律所'}</h2>
              <button className="modal-close" onClick={() => setShowCompanyModal(false)}>
                ×
              </button>
            </div>

            <form onSubmit={handleCompanySubmit}>
              <div className="form-group">
                <label>公司/律所名称 *</label>
                <input
                  type="text"
                  value={companyForm.name}
                  onChange={(e) => setCompanyForm({ ...companyForm, name: e.target.value })}
                  placeholder="例如: XX律师事务所"
                  required
                />
              </div>

              <div className="form-group">
                <label>会议状态/标记</label>
                <select
                  value={companyForm.meeting_status}
                  onChange={(e) => setCompanyForm({ ...companyForm, meeting_status: e.target.value })}
                >
                  {meetingStatusOptions.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label>备注</label>
                <textarea
                  value={companyForm.notes}
                  onChange={(e) => setCompanyForm({ ...companyForm, notes: e.target.value })}
                  placeholder="添加备注信息（如会议记录、联系建议等）..."
                  rows="4"
                />
              </div>

              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => setShowCompanyModal(false)}>
                  取消
                </button>
                <button type="submit" className="btn btn-primary">
                  {editingCompany ? '更新' : '添加'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default Recipients;
